import { Box, Button, Paper, Typography } from "@mui/material";
import { useRef, useState, useEffect, useMemo } from "react";
import * as pdfjsLib from "pdfjs-dist";
import workerSrc from "pdfjs-dist/build/pdf.worker?url";
import { useFormBuilder } from "../formMasterContext";

pdfjsLib.GlobalWorkerOptions.workerSrc = workerSrc;
const PAGE = {
  widthMM: 210,
  heightMM: 297
};

const FormPreview = ({ watchData, setValue, mode = "builder" }: any) => {
  const printRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [pxPerMmX, setPxPerMmX] = useState(3.7795);
  const [pxPerMmY, setPxPerMmY] = useState(3.7795);
  const [pdfDoc, setPdfDoc] = useState<any>(null);
  const [pages, setPages] = useState<number[]>([0]);
  const [currentPage, setCurrentPage] = useState(0);
 
const context = useFormBuilder();
const formData = mode === "builder" ? context?.formData : null;

const fontSize = watchData?.fontSize;
const contextImage = formData?.templateMetadata?.templateImage;
const contextFields = formData?.templateMetadata?.fields;

const rhfImage = watchData?.templateImage;
const templateImage =
  rhfImage && rhfImage !== ""
    ? rhfImage
    : contextImage && typeof contextImage === "string"
      ? contextImage
      : "";

const formattedTemplate = useMemo(() => {
  if (!templateImage) return "";
  if (templateImage.startsWith("data:")) return templateImage;
  if (templateImage.startsWith("JVBER")) return `data:application/pdf;base64,${templateImage}`;
  return `data:image/png;base64,${templateImage}`;
}, [templateImage]);

const rhfFields = watchData?.fields;
const fields =
  rhfFields && rhfFields.length
    ? rhfFields
    : contextFields || [];
const isFetchingForm = watchData?.isFetchingForm || false;

  const [activeElement, setActiveElement] = useState<any>(null);
  const [actionType, setActionType] = useState<"move" | "resize" | null>(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [pdfLoaded, setPdfLoaded] = useState(false);
  const [isColliding, setIsColliding] = useState(false);
  const [originalRect, setOriginalRect] = useState({ xMM: 0, yMM: 0, widthMM: 0, heightMM: 0 });

  // ✅ Measure actual px/mm independently from the rendered Paper to prevent Flexbox Squish Distortion
  useEffect(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setPxPerMmX(rect.width / PAGE.widthMM);
      setPxPerMmY(rect.height / PAGE.heightMM);
    }
  }, []);

  const elements = [...fields];
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const updateElement = (id: any, updates: any) => {
    if (id === "header") {
      setValue("headerPosition", { ...watchData.headerPosition, ...updates });
    } else if (id === "footer") {
      setValue("footerPosition", { ...watchData.footerPosition, ...updates });
    } else {
      const updated = fields.map((f: any) =>
        f.id === id ? { ...f, ...updates } : f
      );
      setValue("fields", updated);
    }
  };

  const renderPage = async (canvas: HTMLCanvasElement, pageIndex: number) => {
    if (!canvas || !pdfDoc) return;

    const page = await pdfDoc.getPage(pageIndex + 1);

    const context = canvas.getContext("2d");
    if (!context) return;

    const SCALE = 800 / 210;
    const viewport = page.getViewport({ scale: SCALE });

    canvas.width = viewport.width;
    canvas.height = viewport.height;

    await page.render({
      canvasContext: context,
      viewport,
    }).promise;
    setPdfLoaded(true);
  };

  const isPdf = useMemo(() => {
    return templateImage?.startsWith("JVBER") ||
           templateImage?.endsWith(".pdf") ||
           templateImage?.startsWith("data:application/pdf") ||
           templateImage?.startsWith("blob:");
  }, [templateImage]);

  // Watch for complete form resets to clear active selections
  useEffect(() => {
    if (fields.length === 0) {
      setActiveElement(null);
      setActionType(null);
    }
  }, [fields]);

  useEffect(() => {
    const loadPdf = async () => {
      if (!templateImage) {
        setPdfDoc(null);
        setPages([0]);
        setCurrentPage(0);
        setPdfLoaded(true);
        return;
      }

      const checkPdf =
        templateImage.startsWith("JVBER") ||
        templateImage.endsWith(".pdf") ||
        templateImage.startsWith("data:application/pdf") ||
        templateImage.startsWith("blob:");

      if (!checkPdf) {
        setPdfDoc(null);
        setPages([0]);
        setCurrentPage(0);
        setPdfLoaded(true);
        return;
      }

      const pdf = await pdfjsLib.getDocument(formattedTemplate).promise;
      setPdfDoc(pdf);

      const pageArray = Array.from({ length: pdf.numPages }, (_, i) => i);
      setPages(pageArray);
    };

    loadPdf();
  }, [templateImage]);

  useEffect(() => {
    if (!canvasRef.current || !pdfDoc) return;
    renderPage(canvasRef.current, currentPage);
  }, [pdfDoc, currentPage]);

  const deleteField = (id: number) => {
    const updated = fields.filter((f: any) => f.id !== id);
    setValue("fields", updated);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "center", width: "100%", position: "relative" }}>
      {(isFetchingForm || (!pdfLoaded && templateImage)) && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(255,255,255,0.85)",
            zIndex: 10,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            backdropFilter: "blur(2px)"
          }}
        >
          <div className="loader" />
          <div style={{ marginTop: 10, fontSize: 14 }}>
            {isFetchingForm ? "Fetching Form..." : "Loading Template..."}
          </div>
        </div>
      )}
      <Paper
        style={{
          width: "210mm",
          height: "297mm",
          minWidth: "210mm",
          minHeight: "297mm",
          position: "relative",
          backgroundSize: "100% 100%"
        }}
      >
        {templateImage && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              zIndex: 0,
            }}
          >
            {isPdf ? (
              <canvas
                ref={canvasRef}
                style={{
                  width: "100%",
                  height: "100%",
                  display: "block"
                }}
              />
            ) : (
              <img
                src={formattedTemplate}
                style={{ width: "100%", height: "100%", objectFit: "fill" }}
                alt="Form Template"
              />
            )}
          </Box>
        )}
        <div
          ref={(node) => {
            // ✅ Attach both refs
            (printRef as any).current = node;
            (containerRef as any).current = node;
          }}
          onClick={(e) => {
            const tool = watchData?.activeTool;
            if (!tool) {
               // ✅ Clear the selected field when clicking empty space to decouple configurations 
               setValue("selectedFieldId", null);
               return;
            }

            const rect = e.currentTarget.getBoundingClientRect();

            const xMM = (e.clientX - rect.left) / pxPerMmX;
            const yMM = (e.clientY - rect.top) / pxPerMmY;

            const baseFontSize = watchData?.fontSize ? Number(watchData.fontSize) : 14;
            const scaleRatio = baseFontSize / 14;

            const newField = {
              id: Date.now(),
              type: tool,
              direction: watchData?.direction?.value || "ltr",
              fontSize: baseFontSize,
              keyName: watchData?.keyName,
              pageIndex: currentPage, // ✅ correct page
              value: "",
              xMM,
              yMM,
              widthMM: tool === "text" ? 60 : 40,
              heightMM: (tool === "text" ? 15 : 5) * scaleRatio,
            };

            const isDroppingOnExisting = elements.some((el) => {
              if (el.pageIndex !== currentPage) return false;
              return (
                newField.xMM < el.xMM + el.widthMM &&
                newField.xMM + newField.widthMM > el.xMM &&
                newField.yMM < el.yMM + el.heightMM &&
                newField.yMM + newField.heightMM > el.yMM
              );
            });

            if (isDroppingOnExisting) {
              setValue("activeTool", null);
              return;
            }

            setValue("fields", [...fields, newField]);

            // optional reset
            setValue("activeTool", null);
            setValue("selectedFieldId", null); // ✅ Clear the selection when adding a new field!
          }}
          style={{ width: "100%", height: "100%", position: "relative", cursor: (isColliding && actionType) ? "not-allowed" : watchData?.activeTool ? "crosshair" : "auto" }}
          onMouseMove={(e) => {
            if (!activeElement) return;

            const rect = e.currentTarget.getBoundingClientRect();

            if (actionType === "move") {
              const xMM = (e.clientX - rect.left - offset.x) / pxPerMmX;
              const yMM = (e.clientY - rect.top - offset.y) / pxPerMmY;

              const collision = elements.some((el) => {
                if (el.id === activeElement.id || el.pageIndex !== currentPage|| el.type === activeElement.type) return false;
                return (
                  xMM < el.xMM + el.widthMM &&
                  xMM + activeElement.widthMM > el.xMM &&
                  yMM < el.yMM + el.heightMM &&
                  yMM + activeElement.heightMM > el.yMM
                );
              });
              setIsColliding(collision);
              updateElement(activeElement.id, { xMM, yMM });
            }

            if (actionType === "resize") {
              const widthMM =
                (e.clientX - rect.left) / pxPerMmX - activeElement.xMM;

              const heightMM =
                (e.clientY - rect.top) / pxPerMmY - activeElement.yMM;

              const collision = elements.some((el) => {
                if (el.id === activeElement.id || el.pageIndex !== currentPage || el.type === activeElement.type) return false;
                return (
                  activeElement.xMM < el.xMM + el.widthMM &&
                  activeElement.xMM + widthMM > el.xMM &&
                  activeElement.yMM < el.yMM + el.heightMM &&
                  activeElement.yMM + heightMM > el.yMM
                );
              });

              setIsColliding(collision);

              // ✅ KEY FIX: lock height for underline
              if (activeElement.type === "underline") {
                updateElement(activeElement.id, {
                  widthMM: Math.max(widthMM, 10), // prevent too small
                  heightMM: activeElement.heightMM // 🔒 keep original height
                });
              } else {
                updateElement(activeElement.id, {
                  widthMM,
                  heightMM
                });
              }
            }
          }}
          onMouseUp={() => {
            if (isColliding && activeElement) {
              updateElement(activeElement.id, {
                xMM: originalRect.xMM,
                yMM: originalRect.yMM,
                widthMM: originalRect.widthMM,
                heightMM: originalRect.heightMM
              });
            }
            setIsColliding(false);
            setActiveElement(null);
            setActionType(null);
          }}
        >
          {pdfLoaded && elements
            .filter((el) => (el.pageIndex ?? 0) === currentPage)
            .map((el) => (
              <div
                key={el.id}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  const rect = e.currentTarget.getBoundingClientRect();
                  setOffset({ x: e.clientX - rect.left, y: e.clientY - rect.top });
                  setValue("selectedFieldId", el.id);
                  setActiveElement(el);
                  setActionType("move");
                  setOriginalRect({ xMM: el.xMM, yMM: el.yMM, widthMM: el.widthMM, heightMM: el.heightMM });
                }}
                style={{
                  position: "absolute",
                  left: el.xMM * pxPerMmX,
                  top: el.yMM * pxPerMmY,
                  width: el.widthMM * pxPerMmX,
                  height: el.heightMM * pxPerMmY,
                  border: (isColliding && activeElement?.id === el.id) ? "2px solid red" : (el.type === "underline" || el.type === "signature" || el.type === "text" ? "none" : "1px dashed black"),
                  backgroundColor: (isColliding && activeElement?.id === el.id) ? "rgba(255,0,0,0.1)" : "transparent",
                  zIndex: activeElement?.id === el.id ? 50 : 1
                }}
              >
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteField(el.id);
                  }}
                  style={{
                    position: "absolute",
                    top: -16,
                    right: -16,
                    width: 18,
                    height: 18,
                    background: "#ff4d4f",
                    color: "#fff",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 12,
                    cursor: "pointer",
                    zIndex: 100,
                    boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                    pointerEvents: "auto"
                  }}
                >
                  ×
                </div>
                {activeElement?.id === el.id && (
                  <div style={{
                    position: "absolute",
                    top: -20,
                    left: 0,
                    fontSize: 11,
                    background: "#000",
                    color: "#fff",
                    padding: "2px 6px",
                    borderRadius: 4,
                    zIndex: 10
                  }}>
                    Chars: {(el.value || "").length}
                  </div>
                )}

                {(el.type === "text" || !el.type) && (
                  <textarea
                    value={el.value || ""}
                    placeholder={activeElement?.id === el.id ? "Add text..." : ""}
                    onChange={(e) => {
                      const node = e.target;
                      const minLineHeight = (el.fontSize || fontSize || 14) * 1.5;
                      const safeHeight = Math.max(node.clientHeight, minLineHeight + 4);

                      // Direct Physical Browser Calculation to physically restrict user inputs from spilling visually beyond the drawn area length
                      if (node.scrollWidth > node.clientWidth || node.scrollHeight > safeHeight) {
                        node.value = el.value || "";
                        return;
                      }
                      updateElement(el.id, { value: node.value });
                    }}
                    style={{
                      width: "100%", height: "100%",
                      overflow: "hidden",
                      fontSize: `${el.fontSize || fontSize}px`,
                      border: "none",
                      boxShadow: activeElement?.id === el.id
                        ? "0px 0px 0px 1.5px #1976d2, 0px 4px 12px rgba(0,0,0,0.1)"
                        : "inset 0px 0px 0px 1px rgba(0,0,0,0.1)",
                      backgroundColor: activeElement?.id === el.id ? "rgba(255, 255, 255, 0.6)" : "transparent",
                      backdropFilter: activeElement?.id === el.id ? "blur(1px)" : "none",
                      borderRadius: activeElement?.id === el.id ? "4px" : "0px",
                      transition: "all 0.2s ease-in-out",
                      resize: "none",
                      outline: "none",
                      fontFamily: "Arial, sans-serif",
                      padding: 0,
                      margin: 0,
                      boxSizing: "border-box",
                      lineHeight: "1.5",
                      color: "#333",
                      direction: el.direction === "rtl" ? "rtl" : "ltr",
                      textAlign: el.direction === "rtl" ? "right" : "left"
                    }}
                  />
                )}

                {el.type === "select" && (
                  <select
                    value={el.value || ""}
                    onChange={(e) => updateElement(el.id, { value: e.target.value })}
                    style={{ width: "100%", height: "100%" }}
                  >
                    {(el.options || ["Yes", "No"]).map((opt: any, i: number) => (
                      <option key={i}>{opt}</option>
                    ))}
                  </select>
                )}

                {el.type === "underline" && (
                  <div style={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "flex-end",  // ✅ pins input to bottom
                  }}>
                    <input
                      value={el.value || ""}
                      onChange={(e) => {
                        const node = e.target;
                        if (node.scrollWidth > node.clientWidth) {
                          node.value = el.value || "";
                          return;
                        }
                        updateElement(el.id, { value: node.value });
                      }}
                      style={{
                        width: "100%",
                        height: "100%",
                        paddingBottom: "2px",
                        border: "none",
                        borderBottom: "2px solid black",
                        fontFamily: "Arial, sans-serif",
                        fontSize: `${ 14}px`,
                        lineHeight: "1",
                        boxSizing: "border-box",
                        margin: 0,
                        padding: 0,
                        color: "#000",
                        background: "transparent",
                        outline: "none",
                        caretColor: "#000",
                        direction: el.direction === "rtl" ? "rtl" : "ltr",
                        textAlign: el.direction === "rtl" ? "right" : "left"
                      }}
                    />
                  </div>
                )}
                {el.type === "image" && (
                  <img
                    src={el.src}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: el.originalType === "signature" ? "fill" : "contain",
                      backgroundColor: "transparent",
                      mixBlendMode: el.originalType === "signature" ? "multiply" : "normal"
                    }}
                    alt="Field content"
                  />
                )}

                {el.type === "signature" && (
                  <div style={{
                    width: "100%", height: "100%",
                    borderBottom: "2px dashed #666",
                    boxSizing: "border-box",
                    fontFamily: "Arial, sans-serif", fontSize: "12px", color: "#666",
                    display: "flex", alignItems: "flex-end", justifyContent: "center",
                    paddingBottom: "4px"
                  }}>
                    
                  </div>
                )}

                <div
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    setActiveElement(el);
                    setActionType("resize");
                    setOriginalRect({ xMM: el.xMM, yMM: el.yMM, widthMM: el.widthMM, heightMM: el.heightMM });
                  }}
                  style={{
                    position: "absolute", right: -15, bottom: -15,
                    width: 16, height: 16,
                    background: activeElement?.id === el.id ? "#1976d2" : "#ccc",
                    cursor: "nwse-resize",
                    borderRadius: "50%", zIndex: activeElement?.id === el.id ? 20 : 10,
                    border: "2px solid white",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.3)"
                  }}
                />
              </div>
            ))}
        </div>
      </Paper>

      <Box
        display="flex"
        width="210mm"
        justifyContent="space-between"
        alignItems="center"
        bgcolor="#f8f9fa"
        p={1.5}
        borderRadius={1}
        border="1px solid #e0e0e0"
      >
        <Box display="flex" gap={2} alignItems="center">
          <Button
            size="small"
            variant="outlined"
            disabled={currentPage === 0}
            onClick={() => setCurrentPage((prev) => prev - 1)}
          >
            Previous
          </Button>

          <Typography variant="body2" sx={{ fontWeight: 600, color: "#444" }}>
            Page {currentPage + 1} of {pages.length || 1}
          </Typography>

          <Button
            size="small"
            variant="outlined"
            disabled={currentPage >= pages.length - 1}
            onClick={() => setCurrentPage((prev) => prev + 1)}
          >
            Next
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default FormPreview;